import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const supabase = createClient(
  "https://jfuxxeegqerhuhgxzlio.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpmdXh4ZWVncWVyaHVoZ3h6bGlvIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjM3OTI2NjAsImV4cCI6MjA3OTM2ODY2MH0.ur266CqIa5Mht67cbZQhOa2yE09K75cBpPyzML8AKsw"
);

interface TermsData {
  id: number;
  type: string;
  title: string;
  content: string;
}

function TermsPage() {
  const location = useLocation();
  const isPrivacy = location.pathname === "/privacy";
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [error, setError] = useState(false);

  const termsType = isPrivacy ? "privacy_policy" : "terms_of_service";
  const defaultTitle = isPrivacy ? "개인정보처리방침" : "이용약관";

  useEffect(() => {
    const fetchTerms = async () => {
      setLoading(true);
      setError(false);

      const { data, error: fetchError } = await supabase
        .from("terms")
        .select("*")
        .eq("type", termsType)
        .single<TermsData>();

      if (fetchError || !data) {
        setError(true);
        setTitle(defaultTitle);
        setContent("내용을 불러오는데 실패했습니다.");
      } else {
        setTitle(data.title);
        setContent(data.content);
      }
      setLoading(false);
    };

    fetchTerms();
  }, [termsType, defaultTitle]);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full bg-gradient-to-r from-[#9b5de5] to-[#f15bb5] shadow-md">
        <div className="container mx-auto px-4 h-16 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-white/20 border border-white/30 shadow-lg backdrop-blur-sm flex items-center justify-center">
              <img
                src="/images/ic_header_logo.svg"
                alt="LOVATA"
                className="w-5 h-5 brightness-0 invert"
              />
            </div>
            <span className="text-xl font-extrabold text-white tracking-wide">
              LOVATA
            </span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-slate-100 max-w-4xl mx-auto">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="w-10 h-10 border-4 border-[#9b5de5] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-8 pb-6 border-b border-slate-100">
                  {title}
                </h1>
                {error ? (
                  <div className="text-center py-12 text-slate-500">
                    {content}
                  </div>
                ) : (
                  <div className="prose prose-slate max-w-none prose-headings:text-slate-800 prose-headings:font-bold prose-h1:text-xl prose-h2:text-lg prose-h3:text-base prose-p:my-3 prose-ul:my-3 prose-li:my-1 prose-a:text-[#9b5de5] prose-table:border-collapse prose-th:border prose-th:border-slate-200 prose-th:bg-slate-50 prose-th:p-2 prose-td:border prose-td:border-slate-200 prose-td:p-2">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {content.replace(/\\n/g, "\n")}
                    </ReactMarkdown>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-[#9b5de5] transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
              홈으로 돌아가기
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-8">
        <div className="container mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2025 LOVATA Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default TermsPage;
